/**
 * kbVersioning — versioni, numerazione delle revisioni e proposta maggiore/minore.
 *
 * Modulo PURO e CONDIVISO.
 *
 * ⚠️ REGOLA DI FONDO: la distinzione fra modifica **sostanziale** e **correzione
 * di forma** la decide l'UMANO, non una soglia di byte. Cambiare «può» in «non
 * può» sono quattro caratteri e ribalta il significato; riformattare un elenco
 * sono ottocento caratteri e non cambia nulla. Qui si calcolano solo dei SEGNALI
 * e si PROPONE una classificazione: la scelta finale arriva dall'editore, con un
 * motivo obbligatorio, e resta scritta nel dato.
 * (È la stessa regola operativa di Wikipedia: è minore solo refuso,
 * punteggiatura e formattazione; se cambia il significato è maggiore.)
 *
 * Doc canonico: docs/01-ARCHITECTURE/KB-CONTENT-MODEL.md
 */

import type { KbTipoModifica } from './kbTypes';

/** Cifre del doc id di una revisione: zero-padding → ordinamento lessicale gratis. */
export const KB_CIFRE_REVISIONE = 6;

/** Doc id della revisione numero `n` (`7` → `'000007'`). */
export function idRevisione(numero: number): string {
  if (!Number.isInteger(numero) || numero < 1) {
    throw new Error(`numero di revisione non valido: ${numero}`);
  }
  return String(numero).padStart(KB_CIFRE_REVISIONE, '0');
}

/** Versione leggibile per il lettore. */
export function formattaVersione(maggiore: number, minore: number): string {
  return `${Math.max(1, maggiore || 1)}.${Math.max(0, minore || 0)}`;
}

/**
 * Versione dopo una modifica.
 * Una maggiore azzera la minore; una minore incrementa solo la minore.
 */
export function versioneSuccessiva(
  attuale: { versione_maggiore: number; versione_minore: number },
  tipo: KbTipoModifica
): { versione_maggiore: number; versione_minore: number } {
  const mag = Math.max(1, attuale.versione_maggiore || 1);
  const min = Math.max(0, attuale.versione_minore || 0);
  return tipo === 'maggiore'
    ? { versione_maggiore: mag + 1, versione_minore: 0 }
    : { versione_maggiore: mag, versione_minore: min + 1 };
}

// ============================================================================
// SEGNALI E PROPOSTA
// ============================================================================

export interface KbSegnaliModifica {
  titolo_cambiato: boolean;
  riassunto_cambiato: boolean;
  /** Intestazioni markdown aggiunte o rimosse: la struttura del contenuto è cambiata. */
  struttura_cambiata: boolean;
  /** Quota di caratteri diversi sul corpo, 0-1. Indicativa, non decisiva. */
  quota_testo_cambiato: number;
}

export interface KbPropostaModifica {
  tipo_proposto: KbTipoModifica;
  segnali: KbSegnaliModifica;
  /** Perché il sistema propone così, in italiano, da mostrare all'editore. */
  spiegazione: string;
}

function contaIntestazioni(markdown: string): number {
  return (markdown.match(/^#{1,6}\s+/gm) ?? []).length;
}

/**
 * Distanza grezza fra due testi: quota di caratteri che differiscono a parità di
 * posizione, più la differenza di lunghezza. Volutamente semplice — non è un
 * diff, è un termometro. Il diff vero lo mostra la console con una libreria.
 */
function quotaDifferenza(prima: string, dopo: string): number {
  if (prima === dopo) return 0;
  const maxLen = Math.max(prima.length, dopo.length);
  if (maxLen === 0) return 0;
  const minLen = Math.min(prima.length, dopo.length);
  let diversi = Math.abs(prima.length - dopo.length);
  for (let i = 0; i < minLen; i++) {
    if (prima.charCodeAt(i) !== dopo.charCodeAt(i)) diversi++;
  }
  return Math.min(1, diversi / maxLen);
}

/**
 * Propone il tipo di modifica. **Default fail-safe: `maggiore`.**
 * È più facile declassare a mano una modifica innocua che accorgersi dopo che una
 * modifica critica non ha avvisato nessuno.
 */
export function classificaModifica(
  prima: { titolo: string; riassunto: string; corpo_markdown: string },
  dopo: { titolo: string; riassunto: string; corpo_markdown: string }
): KbPropostaModifica {
  const segnali: KbSegnaliModifica = {
    titolo_cambiato: prima.titolo.trim() !== dopo.titolo.trim(),
    riassunto_cambiato: prima.riassunto.trim() !== dopo.riassunto.trim(),
    struttura_cambiata:
      contaIntestazioni(prima.corpo_markdown) !== contaIntestazioni(dopo.corpo_markdown),
    quota_testo_cambiato: quotaDifferenza(prima.corpo_markdown, dopo.corpo_markdown),
  };

  const motivi: string[] = [];
  if (segnali.titolo_cambiato) motivi.push('è cambiato il titolo');
  if (segnali.riassunto_cambiato) motivi.push('è cambiato il riassunto');
  if (segnali.struttura_cambiata) motivi.push('sono cambiate le sezioni');
  if (segnali.quota_testo_cambiato >= 0.15) {
    motivi.push('è cambiata una parte consistente del testo');
  }

  if (motivi.length > 0) {
    return {
      tipo_proposto: 'maggiore',
      segnali,
      spiegazione: `sembra un aggiornamento del contenuto: ${motivi.join(', ')}`,
    };
  }

  if (segnali.quota_testo_cambiato === 0) {
    return {
      tipo_proposto: 'minore',
      segnali,
      spiegazione: 'il testo non è cambiato',
    };
  }

  return {
    tipo_proposto: 'minore',
    segnali,
    spiegazione:
      'sembra una correzione di forma: poche modifiche al testo, nessun cambio di titolo o di sezioni',
  };
}

/**
 * Serve una conferma esplicita dall'editore?
 * Sì quando dichiara «correzione di forma» ma i segnali dicono il contrario: in
 * quel caso la console mostra il diff e chiede di confermare. Non si corregge la
 * scelta dell'umano a sua insaputa.
 */
export function richiedeConfermaSuMinore(
  tipoScelto: KbTipoModifica,
  proposta: KbPropostaModifica
): boolean {
  return tipoScelto === 'minore' && proposta.tipo_proposto === 'maggiore';
}
